import { getAuth } from "firebase/auth";
import { logger } from '../utils/logger';
import { authAxios } from '../utils/apiConfig';

const keywordAxios = authAxios;

// Get keyword overview data
export async function getKeywordOverview(keyword, country = 'US') {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      logger.error('No authenticated user found');
      throw new Error('Authentication required');
    }

    const response = await keywordAxios.get('/spyfu/keyword-overview', {
      params: {
        user: user.email,
        keyword: keyword,
        country: country
      }
    });

    return response.data;
  } catch (error) {
    logger.error("Error in getKeywordOverview:", error);
    if (error.response) {
      logger.error("Server response:", error.response.data);
      throw new Error(error.response.data.message || error.response.data.detail || 'Server error');
    }
    throw error;
  }
}

// Get keyword suggestions
export async function getKeywordSuggestions(keyword, country = 'US', page = 1, pageSize = 100) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      logger.error('No authenticated user found');
      throw new Error('Authentication required');
    }

    const response = await keywordAxios.get('/spyfu/keyword-suggestions', {
      params: {
        user: user.email,
        keyword: keyword,
        country: country,
        page: page,
        page_size: pageSize
      }
    });

    return response.data;
  } catch (error) {
    logger.error("Error in getKeywordSuggestions:", error);
    if (error.response) {
      logger.error("Server response:", error.response.data);
      throw new Error(error.response.data.message || error.response.data.detail || 'Server error');
    }
    throw error;
  }
}

// Get related keywords
export async function getRelatedKeywords(keyword, country = 'US', page = 1, pageSize = 100) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      logger.error('No authenticated user found');
      throw new Error('Authentication required');
    }

    const response = await keywordAxios.get('/spyfu/related-keywords', {
      params: {
        user: user.email,
        keyword: keyword,
        country: country,
        page: page,
        page_size: pageSize
      }
    });

    return response.data;
  } catch (error) {
    logger.error("Error in getRelatedKeywords:", error);
    if (error.response) {
      logger.error("Server response:", error.response.data);
      throw new Error(error.response.data.message || error.response.data.detail || 'Server error');
    }
    throw error;
  }
}

// Get domain stats for traffic analysis
export async function getDomainStats(domain, country = 'US') {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      logger.error('No authenticated user found');
      throw new Error('Authentication required');
    }

    const response = await keywordAxios.get('/spyfu/domain-stats', {
      params: {
        user: user.email,
        domain: domain,
        country: country
      }
    });

    return response.data;
  } catch (error) {
    logger.error("Error in getDomainStats:", error);
    if (error.response) {
      logger.error("Server response:", error.response.data);
      throw new Error(error.response.data.message || error.response.data.detail || 'Server error');
    }
    throw error;
  }
}

// Get SERP analysis for a keyword
export async function getSerpAnalysis(keyword, country = 'US') {
  try {
    // Use keyword overview to get SERP features data
    const response = await getKeywordOverview(keyword, country);
    
    if (response.success && response.data?.results?.length > 0) {
      const keywordData = response.data.results[0];
      return {
        success: true,
        data: {
          serpFeatures: keywordData.serpFeaturesCsv ? keywordData.serpFeaturesCsv.split(',') : [],
          firstResult: keywordData.serpFirstResult,
          isQuestion: keywordData.isQuestion,
          competitorCount: keywordData.paidCompetitors || 0,
          organicClicks: keywordData.totalMonthlyClicks || 0,
          organicClickPercent: keywordData.percentOrganicClicks || 0,
          paidClickPercent: keywordData.percentPaidClicks || 0
        }
      };
    }
    
    return { success: false, error: 'No SERP data available' };
  } catch (error) {
    logger.error("Error in getSerpAnalysis:", error);
    throw error;
  }
}

// Calculate traffic potential for a keyword at different rankings
export function calculateTrafficPotential(searchVolume, organicClickPercent, targetRanking = 1) {
  const clickThroughRates = {
    1: 0.316,   // Position 1: 31.6% CTR
    2: 0.157,   // Position 2: 15.7% CTR  
    3: 0.111,   // Position 3: 11.1% CTR
    4: 0.073,   // Position 4: 7.3% CTR
    5: 0.055,   // Position 5: 5.5% CTR
    6: 0.040,   // Position 6: 4.0% CTR
    7: 0.030,   // Position 7: 3.0% CTR
    8: 0.024,   // Position 8: 2.4% CTR
    9: 0.020,   // Position 9: 2.0% CTR
    10: 0.017   // Position 10: 1.7% CTR
  };
  
  const ctr = clickThroughRates[targetRanking] || 0.01; // Default 1% for positions 11+
  const organicMultiplier = organicClickPercent ? (organicClickPercent / 100) : 0.6; // Default 60% organic clicks
  
  return {
    estimatedMonthlyClicks: Math.round(searchVolume * ctr * organicMultiplier),
    clickThroughRate: ctr,
    organicShare: organicMultiplier,
    potential: {
      position1: Math.round(searchVolume * 0.316 * organicMultiplier),
      position3: Math.round(searchVolume * 0.111 * organicMultiplier),
      position5: Math.round(searchVolume * 0.055 * organicMultiplier),
      position10: Math.round(searchVolume * 0.017 * organicMultiplier)
    }
  };
}

// Get organic keywords for a domain
export async function getDomainOrganicKeywords(domain, country = 'US', page = 1, pageSize = 100) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      logger.error('No authenticated user found');
      throw new Error('Authentication required');
    }

    const response = await keywordAxios.get('/spyfu/organic-keywords', {
      params: {
        user: user.email,
        domain: domain,
        country: country,
        page: page,
        page_size: pageSize
      }
    });

    return response.data;
  } catch (error) {
    logger.error("Error in getDomainOrganicKeywords:", error);
    if (error.response) {
      logger.error("Server response:", error.response.data);
      throw new Error(error.response.data.message || error.response.data.detail || 'Server error');
    }
    throw error;
  }
}

// Get paid keywords for a domain
export async function getDomainPaidKeywords(domain, country = 'US', page = 1, pageSize = 100) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      logger.error('No authenticated user found');
      throw new Error('Authentication required');
    }

    const response = await keywordAxios.get('/spyfu/paid-keywords', {
      params: {
        user: user.email,
        domain: domain,
        country: country,
        page: page,
        page_size: pageSize
      }
    });

    return response.data;
  } catch (error) {
    logger.error("Error in getDomainPaidKeywords:", error);
    if (error.response) {
      logger.error("Server response:", error.response.data);
      throw new Error(error.response.data.message || error.response.data.detail || 'Server error');
    }
    throw error;
  }
}

// Get domain overview
export async function getDomainOverview(domain, country = 'US') {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      logger.error('No authenticated user found');
      throw new Error('Authentication required');
    }

    const response = await keywordAxios.get('/spyfu/domain-overview', {
      params: {
        user: user.email,
        domain: domain,
        country: country
      }
    });

    return response.data;
  } catch (error) {
    logger.error("Error in getDomainOverview:", error);
    if (error.response) {
      logger.error("Server response:", error.response.data);
      throw new Error(error.response.data.message || error.response.data.detail || 'Server error');
    }
    throw error;
  }
}

// Get domain competitors
export async function getDomainCompetitors(domain, organic = true, country = 'US') {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      logger.error('No authenticated user found');
      throw new Error('Authentication required');
    }

    const response = await keywordAxios.get('/spyfu/competitors', {
      params: {
        user: user.email,
        domain: domain,
        organic: organic,
        country: country
      }
    });

    return response.data;
  } catch (error) {
    logger.error("Error in getDomainCompetitors:", error);
    if (error.response) {
      logger.error("Server response:", error.response.data);
      throw new Error(error.response.data.message || error.response.data.detail || 'Server error');
    }
    throw error;
  }
}

// Export all functions as default object
const keywordIntelligenceService = {
  getKeywordOverview,
  getKeywordSuggestions,
  getRelatedKeywords,
  getDomainStats,
  getSerpAnalysis,
  calculateTrafficPotential,
  getDomainOrganicKeywords,
  getDomainPaidKeywords,
  getDomainOverview,
  getDomainCompetitors
};

export default keywordIntelligenceService;
